"use client";

import { useState } from "react";
import {
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  BreakdownView,
  EntryCard,
} from "@/components/tools/word-knowledge/roots-parts";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  analyzeWord,
  type KindFilter,
  type OriginFilter,
  type PartKind,
  type RootEntry,
  searchRoots,
} from "@/lib/tool-kit/word-knowledge/roots";

/** English strings. A translated content file overrides these through `ui`. */
export const GREEK_AND_LATIN_ROOTS_STRINGS = {
  modeLabel: "What do you want to do?",
  modes: {
    search: "Search roots and affixes",
    split: "Break a word into parts",
  } as Record<Mode, string>,
  searchLabel: "Search",
  searchPlaceholder: "dict, water, -ology, biography",
  searchHint:
    "Type a root, prefix or suffix, a meaning such as “water”, or an example word.",
  kindLabel: "Type",
  kinds: {
    all: "All",
    root: "Roots",
    prefix: "Prefixes",
    suffix: "Suffixes",
  } as Record<KindFilter, string>,
  originLabel: "Origin",
  origins: {
    all: "Greek and Latin",
    greek: "Greek",
    latin: "Latin",
  } as Record<OriginFilter, string>,
  resultsHeading: "Roots and affixes",
  resultCount: "one:{count} entry|other:{count} entries",
  showMore: "Show {count} more",
  noResults:
    "Nothing matches “{query}”. Try the root’s spelling without a hyphen, a one-word meaning, or an example word.",
  kindNames: {
    root: "root",
    prefix: "prefix",
    suffix: "suffix",
  } as Record<PartKind, string>,
  originNames: {
    greek: "Greek",
    latin: "Latin",
    both: "Greek and Latin",
    english: "English",
  } as Record<RootEntry["origin"], string>,
  alsoSpelled: "Also spelled: {forms}",
  sourceLine: "From {source}",
  examplesLabel: "Examples (click to break down):",
  wordLabel: "Word",
  wordPlaceholder: "biography",
  wordHint:
    "One word of four to forty letters. Prefixes and endings are found too.",
  tryLabel: "Try:",
  examples: [
    "biography",
    "transportation",
    "unpredictable",
    "photosynthesis",
    "benevolent",
  ],
  splitPrompt:
    "Type a word to see it split into prefix, root and suffix, with the meaning of each part.",
  splitHeading: "“{word}” in parts",
  summaryLabel: "Built from:",
  partLabels: {
    root: "root",
    prefix: "prefix",
    suffix: "suffix",
    glue: "joining vowel",
    unknown: "not recognized",
  } as Record<PartKind | "glue" | "unknown", string>,
  helperLabels: {
    prefix: "English prefix",
    suffix: "English ending",
  } as Record<"prefix" | "suffix", string>,
  glueMeaning:
    "A vowel that joins two Greek or Latin parts so the word is easy to say.",
  unknownMeaning: "Not a Greek or Latin part in this list.",
  alternativeNote:
    "Another entry has this spelling: {others}. Check which sense fits the word.",
  coverageNote:
    "Some letters are not explained: they may come from a part this list does not have, or from English itself.",
  caution:
    "A pattern match, not an etymology. Words change as they pass through French and Old English, so confirm the history of anything you will rely on in a dictionary such as Etymonline or Wiktionary.",
  noneFound:
    "“{word}” could not be read as Greek or Latin parts. It may be an ordinary English word, or built from a part this list does not have.",
  lookalike:
    "“{word}” is an everyday English word with its own history. It only looks as if it contains a Greek or Latin part.",
  invalid: "Enter one word of four to forty letters.",
  sourceNote:
    "Entries and example words are original to CitationEasy, with source words checked against Wiktionary and Etymonline.",
} as const;

type Strings = typeof GREEK_AND_LATIN_ROOTS_STRINGS;
type Mode = "search" | "split";

const PAGE_SIZE = 24;
const MAX_INPUT = 40;
const KINDS: KindFilter[] = ["all", "root", "prefix", "suffix"];
const ORIGINS: OriginFilter[] = ["all", "greek", "latin"];
const MODES: Mode[] = ["search", "split"];

function SplitResult({ word, strings }: { word: string; strings: Strings }) {
  const analysis = analyzeWord(word);
  if (analysis.status === "parts") {
    return (
      <div aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>
          {t(strings.splitHeading, { word: analysis.breakdown.word })}
        </h2>
        <BreakdownView breakdown={analysis.breakdown} strings={strings} />
      </div>
    );
  }
  let message: string = strings.invalid;
  if (analysis.status === "none") {
    message = t(strings.noneFound, { word: analysis.word });
  } else if (analysis.status === "lookalike") {
    message = t(strings.lookalike, { word: analysis.word });
  }
  return <Notice>{message}</Notice>;
}

function SearchResults({
  query,
  kind,
  origin,
  strings,
  onBreak,
}: {
  query: string;
  kind: KindFilter;
  origin: OriginFilter;
  strings: Strings;
  onBreak: (word: string) => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [shown, setShown] = useState(PAGE_SIZE);
  const results = searchRoots({ query, kind, origin });
  const visible = results.slice(0, shown);
  if (results.length === 0) {
    return <Notice>{t(strings.noResults, { query })}</Notice>;
  }
  const remaining = results.length - visible.length;
  return (
    <div className="space-y-4">
      <div aria-live="polite" className="space-y-1">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <p className={HINT}>
          {tPlural(strings.resultCount, locale, results.length, {
            count: fmt(results.length),
          })}
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {visible.map((entry) => (
          <EntryCard
            entry={entry}
            key={entry.id}
            onBreak={onBreak}
            strings={strings}
          />
        ))}
      </div>
      {remaining > 0 ? (
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setShown((count) => count + PAGE_SIZE * 4)}
          type="button"
        >
          {t(strings.showMore, {
            count: fmt(Math.min(remaining, PAGE_SIZE * 4)),
          })}
        </button>
      ) : null}
    </div>
  );
}

export default function GreekAndLatinRootsWidget() {
  const strings = useToolUi(GREEK_AND_LATIN_ROOTS_STRINGS);
  const [mode, setMode] = useState<Mode>("search");
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<KindFilter>("all");
  const [origin, setOrigin] = useState<OriginFilter>("all");
  const [word, setWord] = useState("");

  function breakDown(next: string) {
    setWord(next);
    setMode("split");
  }

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="roots-mode"
        onChange={setMode}
        options={MODES.map((value) => ({ value, label: strings.modes[value] }))}
        value={mode}
      />
      {mode === "split" ? (
        <div className="space-y-4">
          <TextField
            hint={strings.wordHint}
            id="roots-word"
            label={strings.wordLabel}
            onChange={(value) => setWord(value.slice(0, MAX_INPUT))}
            placeholder={strings.wordPlaceholder}
            type="search"
            value={word}
          />
          {word.trim() ? (
            <SplitResult strings={strings} word={word} />
          ) : (
            <div className="space-y-3">
              <Notice>{strings.splitPrompt}</Notice>
              <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
                <span className="text-muted-foreground">
                  {strings.tryLabel}
                </span>
                {strings.examples.map((example) => (
                  <button
                    className="font-medium text-primary underline-offset-4 hover:underline"
                    key={example}
                    onClick={() => setWord(example)}
                    type="button"
                  >
                    {example}
                  </button>
                ))}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <TextField
            hint={strings.searchHint}
            id="roots-search"
            label={strings.searchLabel}
            onChange={(value) => setQuery(value.slice(0, MAX_INPUT))}
            placeholder={strings.searchPlaceholder}
            type="search"
            value={query}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="roots-kind"
              label={strings.kindLabel}
              onChange={setKind}
              options={KINDS.map((value) => ({
                value,
                label: strings.kinds[value],
              }))}
              value={kind}
            />
            <SelectField
              id="roots-origin"
              label={strings.originLabel}
              onChange={setOrigin}
              options={ORIGINS.map((value) => ({
                value,
                label: strings.origins[value],
              }))}
              value={origin}
            />
          </div>
          <SearchResults
            key={`${query}|${kind}|${origin}`}
            kind={kind}
            onBreak={breakDown}
            origin={origin}
            query={query}
            strings={strings}
          />
        </div>
      )}
      <p className={HINT}>{strings.sourceNote}</p>
    </>
  );
}
